import type { JSONSchema7 } from "json-schema";
import type { NodeTypeRegistry } from "./NodeTypeRegistry";

export type NodeId = string;
export type EdgeId = string;
export type SocketId = string;

// EventEmitter interface for type safety
// Following the pattern used by Vue, mitt, and other modern event emitters
// We use conditional types to handle the function parameters safely
export interface IEventEmitter<Events = any> {
  on<K extends keyof Events>(
    event: K,
    handler: Events[K] extends (...args: any[]) => any ? Events[K] : never,
  ): void;
  off<K extends keyof Events>(
    event: K,
    handler?: Events[K] extends (...args: any[]) => any ? Events[K] : never,
  ): void;
  once<K extends keyof Events>(
    event: K,
    handler: Events[K] extends (...args: any[]) => any ? Events[K] : never,
  ): void;
  emit<K extends keyof Events>(
    event: K,
    ...args: Events[K] extends (...args: infer P) => any ? P : never[]
  ): void;
  listeners<K extends keyof Events>(event: K): Array<Events[K]>;
  removeAllListeners<K extends keyof Events>(event?: K): void;
  listenerCount<K extends keyof Events>(event: K): number;
  eventNames(): Array<keyof Events>;
}

export enum SocketDirection {
  Input = "input",
  Output = "output",
}

export interface Socket {
  id: SocketId;
  nodeId: NodeId;
  direction: SocketDirection;
  schema: JSONSchema7; // JSON Schema for validation
  type?: string; // Legacy support, will be deprecated
  multiple?: boolean; // Allow multiple connections
  required?: boolean; // Required for node execution
  defaultValue?: any; // Default value when not connected
  metadata?: Record<string, unknown>; // Additional UI/display metadata
}

export interface Node {
  id: NodeId;
  type: string;
  position?: { x: number; y: number }; // Visual position (industry standard)
  metadata?: Record<string, unknown>;
}

export interface Edge {
  id: EdgeId;
  source: {
    nodeId: NodeId;
    socketId: SocketId;
  };
  target: {
    nodeId: NodeId;
    socketId: SocketId;
  };
  metadata?: Record<string, unknown>;
}

// Validation severity levels
export enum ValidationSeverity {
  ERROR = "error", // Real error (type mismatch, incompatible schemas)
  WARNING = "warning", // Possible issue but not blocking
  RESTRICTION = "restriction", // Business rule restriction (multiplicity, blacklist)
}

// Validation problem categories
export enum ValidationCategory {
  TYPE_MISMATCH = "type_mismatch", // Schema/type incompatibility
  MULTIPLICITY = "multiplicity", // Multiple connections to single-input
  CYCLE_DETECTED = "cycle_detected", // Would create a cycle
  CUSTOM_RULE = "custom_rule", // Custom validator rejection
  BLACKLIST = "blacklist", // Future: blacklisted connection
}

// Connection validation result
export interface ConnectionValidationResult {
  valid: boolean;
  reason?: string;
  severity?: ValidationSeverity;
  category?: ValidationCategory;
  errors?: Array<{
    code: string;
    message: string;
    path?: string;
    suggestion?: string;
    sourceSchema?: any;
    targetSchema?: any;
  }>;
  warnings?: Array<{
    code: string;
    message: string;
    path?: string;
    suggestion?: string;
  }>;
}

// Validation target result
export interface ValidationTarget {
  nodeId: NodeId;
  socketId: SocketId;
  valid: boolean;
  reason?: string;
  pending?: boolean; // Still being validated
  severity?: ValidationSeverity; // NEW
  category?: ValidationCategory; // NEW
}

// Edge validation result
export interface EdgeValidationResult {
  edgeId: EdgeId;
  valid: boolean;
  reason?: string;
  timestamp: number;
  severity?: ValidationSeverity; // NEW: differentiate error types
  category?: ValidationCategory; // NEW: categorize the problem
  errors?: Array<{
    code: string;
    message: string;
    path?: string;
    suggestion?: string;
    sourceSchema?: any;
    targetSchema?: any;
  }>;
  warnings?: Array<{
    code: string;
    message: string;
    path?: string;
    suggestion?: string;
  }>;
}

// Edge finding session info
export interface EdgeFindingSession {
  sourceNodeId: NodeId;
  sourceSocketId: SocketId;
  totalTargets: number;
  validatedTargets: number;
  validTargets: ValidationTarget[];
  invalidTargets: ValidationTarget[];
  pendingTargets: ValidationTarget[];
  startedAt: number;
  completedAt?: number;
}

export interface GraphEvents {
  // Core structural events (using namespace:event pattern)
  "node:added": (node: Node) => void;
  "node:removed": (nodeId: NodeId, node: Node) => void; // Now includes removed node
  "node:updated": (nodeId: NodeId, node: Node) => void;
  "edge:added": (edge: Edge) => void;
  "edge:removed": (edgeId: EdgeId, edge: Edge) => void; // Now includes removed edge
  "edge:updated": (edgeId: EdgeId, edge: Edge) => void;

  // Graph-level events
  "graph:beforeClear": (stats: {
    nodeCount: number;
    edgeCount: number;
  }) => void;
  "graph:cleared": (stats: { nodeCount: number; edgeCount: number }) => void;

  // Legacy/deprecated events (kept for compatibility - will be removed in v2)
  "legacy:nodeDirtyStateChanged": (nodeId: NodeId, isDirty: boolean) => void;
  "legacy:groupCreated": (groupId: NodeId, nodeIds: NodeId[]) => void;
  "legacy:groupDissolved": (groupId: NodeId, nodeIds: NodeId[]) => void;

  // Edge finding events
  "edgeFinding:started": (session: EdgeFindingSession) => void;
  "edgeFinding:progress": (
    session: EdgeFindingSession,
    target: ValidationTarget,
  ) => void;
  "edgeFinding:completed": (session: EdgeFindingSession) => void;
  "edgeFinding:cancelled": () => void;

  // Edge validation events
  "edgeValidation:changed": (
    edgeId: EdgeId,
    valid: boolean,
    reason?: string,
  ) => void;
  "graphValidation:started": (totalEdges: number) => void;
  "graphValidation:progress": (
    validated: number,
    total: number,
    edgeId: EdgeId,
  ) => void;
  "graphValidation:completed": (invalidEdges: EdgeId[]) => void;
}

// Special node types for groups (following Houdini/Blender patterns)
export enum SpecialNodeType {
  GROUP = "group",
  GROUP_INPUT = "group_input",
  GROUP_OUTPUT = "group_output",
}

// Boundary crossing analysis for group creation
export interface BoundaryInput {
  edgeId: EdgeId;
  externalSource: { nodeId: NodeId; socketId: SocketId };
  targetNodeId: NodeId;
  targetSocketId: SocketId;
  socketType?: string;
}

export interface BoundaryOutput {
  edgeId: EdgeId;
  sourceNodeId: NodeId;
  sourceSocketId: SocketId;
  externalTarget: { nodeId: NodeId; socketId: SocketId };
  socketType?: string;
}

export interface BoundaryAnalysis {
  inputs: BoundaryInput[];
  outputs: BoundaryOutput[];
}

export interface SerializedGraph {
  nodes: Node[];
  edges: Edge[];
  metadata?: Record<string, unknown>;
}

// Validation interfaces
export interface IConnectionValidator {
  canConnect(
    sourceNode: Node,
    sourceSocket: Socket,
    targetNode: Node,
    targetSocket: Socket,
  ): boolean | ConnectionValidationResult;
}

export interface ISocketTypeValidator {
  areTypesCompatible(
    sourceType: string | undefined,
    targetType: string | undefined,
  ): boolean;
}

// History management interface
export interface IHistoryManager {
  push(action: HistoryAction): void;
  undo(): HistoryAction | undefined;
  redo(): HistoryAction | undefined;
  canUndo(): boolean;
  canRedo(): boolean;
  clear(): void;
}

export type HistoryAction =
  | { type: "nodeAdded"; data: Node }
  | { type: "nodeRemoved"; data: Node }
  | { type: "nodeUpdated"; data: { before: Node; after: Node } }
  | { type: "edgeAdded"; data: Edge }
  | { type: "edgeRemoved"; data: Edge }
  | { type: "edgeUpdated"; data: { before: Edge; after: Edge } };

// Graph configuration
export interface GraphConfig {
  registry?: NodeTypeRegistry;
  validator?: IConnectionValidator;
  typeValidator?: ISocketTypeValidator;
  historyManager?: IHistoryManager;
  metadata?: Record<string, unknown>;
  dirtyPropagationStrategy?: "immediate" | "lazy";
  enableDirtyDebug?: boolean;
  // Cache configuration
  cacheMaxEntries?: number;
  cacheEnableImmutability?: boolean;
  cacheEvictionPolicy?: "lru" | "lfu" | "fifo";
  cacheTTL?: number;
  // Groups feature (disabled by default for backward compatibility)
  enableGroups?: boolean;
  // Event system - allows sharing EventEmitter between Graph and DAGGraph
  eventEmitter?: IEventEmitter<
    GraphEvents & Record<string, (...args: any[]) => void>
  >;
}
